import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  ThumbsUp, Heart, Smiley, SmileySad, SmileyAngry, Lightning,
  ChatCircle, Trash, LinkSimple, Share, DotsThree,
} from "@phosphor-icons/react";
import VerifiedBadge from "@/components/VerifiedBadge";
import UserBadge from "@/components/UserBadge";
import AchievementBadges from "@/components/AchievementBadges";
import EmojiPicker from "@/components/feed/EmojiPicker";
import UserHoverCard from "@/components/feed/UserHoverCard";
import LikesModal from "@/components/feed/LikesModal";

const REACTIONS = [
  { key: "like", label: "Like", color: "text-blue-500", Icon: ThumbsUp, emoji: "👍" },
  { key: "love", label: "Love", color: "text-red-500", Icon: Heart, emoji: "❤️" },
  { key: "haha", label: "Haha", color: "text-yellow-500", Icon: Smiley, emoji: "😆" },
  { key: "wow", label: "Wow", color: "text-orange-500", Icon: Lightning, emoji: "😮" },
  { key: "sad", label: "Sad", color: "text-slate-500", Icon: SmileySad, emoji: "😢" },
  { key: "angry", label: "Angry", color: "text-rose-600", Icon: SmileyAngry, emoji: "😡" },
];

function resolveImg(u) {
  if (!u) return "";
  if (u.startsWith("/api/")) return `${API_BASE.replace(/\/api$/, "")}${u}`;
  return u;
}

function formatTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

function Avatar({ user, size = 44 }) {
  const src = resolveImg(user?.avatar_url);
  const letter = (user?.username || "?").slice(0, 1).toUpperCase();
  return src ? (
    <img src={src} alt={user?.username} style={{ width: size, height: size }} className="rounded-full object-cover shrink-0" />
  ) : (
    <div style={{ width: size, height: size }} className="rounded-full bg-gradient-to-br from-blue-600 to-blue-800 text-white flex items-center justify-center font-black shrink-0">
      {letter}
    </div>
  );
}

export default function PostCard({ post, onDelete, onUpdate }) {
  const { user } = useAuth();
  const [showReactions, setShowReactions] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState(null);
  const [commentBody, setCommentBody] = useState("");
  const [posting, setPosting] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [likesOpen, setLikesOpen] = useState(false);

  const isMine = post.user_id === user?.id;
  const canDelete = isMine || user?.role === "admin";
  const viewerReaction = post.viewer_reaction;
  const viewerReactionMeta = REACTIONS.find((r) => r.key === viewerReaction);

  const topReactions = [...REACTIONS]
    .map((r) => ({ ...r, count: post.reaction_counts?.[r.key] || 0 }))
    .filter((r) => r.count > 0)
    .sort((a, b) => b.count - a.count)
    .slice(0, 3);

  // Long-press detection so touch users can access the reaction picker.
  // Desktop still gets the picker via hover (onMouseEnter/Leave on the wrapper).
  const longPressTimer = useRef(null);
  const longPressFired = useRef(false);
  const startLongPress = () => {
    longPressFired.current = false;
    clearTimeout(longPressTimer.current);
    longPressTimer.current = setTimeout(() => {
      longPressFired.current = true;
      setShowReactions(true);
    }, 350);
  };
  const cancelLongPress = () => {
    clearTimeout(longPressTimer.current);
  };

  async function react(key) {
    setShowReactions(false);
    try {
      const nextKey = viewerReaction === key ? null : key;
      const { data } = await api.post(`/feed/${post.id}/react`, { reaction: nextKey });
      onUpdate?.(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  async function loadComments() {
    try {
      const { data } = await api.get(`/feed/${post.id}/comments`);
      setComments(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  function toggleComments() {
    const next = !showComments;
    setShowComments(next);
    if (next && comments === null) loadComments();
  }

  async function submitComment(e) {
    e.preventDefault();
    if (!commentBody.trim()) return;
    setPosting(true);
    try {
      const { data } = await api.post(`/feed/${post.id}/comments`, { body: commentBody.trim() });
      setComments((c) => [...(c || []), data]);
      onUpdate?.({ ...post, comments_count: (post.comments_count || 0) + 1 });
      setCommentBody("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPosting(false);
    }
  }

  async function deleteComment(cid) {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await api.delete(`/feed/comments/${cid}`);
      setComments((c) => c.filter((x) => x.id !== cid));
      onUpdate?.({ ...post, comments_count: Math.max(0, (post.comments_count || 0) - 1) });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  async function likeComment(cid, reactionKey = null) {
    try {
      const body = reactionKey === null ? {} : { reaction: reactionKey };
      const { data } = await api.post(`/feed/comments/${cid}/react`, body);
      setComments((cs) => cs.map((c) => c.id === cid ? {
        ...c,
        viewer_reaction: data.viewer_reaction,
        viewer_liked: data.liked,
        reaction_counts: data.reaction_counts,
        reactions_total: data.reactions_total,
        likes_count: data.likes_count,
      } : c));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  async function deletePost() {
    if (!window.confirm("Delete this post permanently?")) return;
    try {
      await api.delete(`/feed/${post.id}`);
      onDelete?.(post.id);
      toast.success("Post deleted");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  async function copyLink() {
    const link = post.url || window.location.href;
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Link copied");
    } catch {
      toast.error("Copy failed");
    }
    setMenuOpen(false);
  }

  const author = post.author || { id: post.user_id, username: "user" };
  const imgSrc = resolveImg(post.image_url);

  return (
    <>
    <article
      data-testid={`feed-post-${post.id}`}
      className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm overflow-hidden"
    >
      {/* Header */}
      <div className="p-4 flex items-start gap-3">
        <Link to={`/u/${author.id}`} className="hover:opacity-90 transition-opacity">
          <Avatar user={author} />
        </Link>
        <div className="flex-1 min-w-0">
          <UserHoverCard userId={author.id} username={author.username}>
            <Link
              to={`/u/${author.id}`}
              data-testid={`post-author-link-${post.id}`}
              className="font-black text-slate-900 dark:text-white hover:underline inline-flex items-center gap-1"
            >
              {author.username}
              <UserBadge user={author} size={14} />
              <AchievementBadges userId={author.id} size={14} />
            </Link>
          </UserHoverCard>
          <div className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-2">
            <span>{formatTime(post.created_at)}</span>
            <span>•</span>
            <span className="capitalize">{post.category || "general"}</span>
            {author.membership && author.membership !== "Free" && (
              <>
                <span>•</span>
                <span className="inline-flex items-center gap-1 text-yellow-600 font-bold">
                  {author.membership}
                </span>
              </>
            )}
          </div>
        </div>
        <div className="relative">
          <button
            data-testid={`post-menu-btn-${post.id}`}
            onClick={() => setMenuOpen((v) => !v)}
            className="h-8 w-8 inline-flex items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
            aria-label="More options"
          >
            <DotsThree size={22} weight="bold" />
          </button>
          {menuOpen && (
            <div className="absolute right-0 top-full mt-1 z-10 w-44 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg py-1">
              <button onClick={copyLink} className="w-full text-left px-3 py-2 text-sm hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center gap-2">
                <Share size={14} weight="bold" /> Copy link
              </button>
              {canDelete && (
                <button
                  data-testid={`post-delete-btn-${post.id}`}
                  onClick={() => { setMenuOpen(false); deletePost(); }}
                  className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 flex items-center gap-2"
                >
                  <Trash size={14} weight="bold" /> Delete post
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="px-4 pb-3">
        <p className="text-slate-900 dark:text-slate-100 whitespace-pre-wrap text-[15px] leading-relaxed">
          {post.body}
        </p>
        {post.url && (
          <a
            href={post.url}
            target="_blank"
            rel="noreferrer noopener"
            data-testid={`post-url-${post.id}`}
            className="mt-2 inline-flex items-center gap-1 text-sm text-blue-700 dark:text-blue-400 hover:underline break-all"
          >
            <LinkSimple size={14} weight="bold" /> {post.url}
          </a>
        )}
      </div>

      {imgSrc && (
        <a href={post.url || imgSrc} target="_blank" rel="noreferrer noopener" className="block">
          <img src={imgSrc} alt="" className="w-full max-h-[520px] object-cover" />
        </a>
      )}

      {/* Counts */}
      <div className="px-4 pt-3 pb-2 flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
        <div className="flex items-center gap-1">
          {topReactions.length > 0 ? (
            <button
              data-testid={`post-reactions-total-${post.id}`}
              onClick={() => setLikesOpen(true)}
              className="inline-flex items-center gap-1 hover:underline"
              title="See who reacted"
            >
              <div className="flex -space-x-1">
                {topReactions.map((r) => (
                  <span key={r.key} className="h-5 w-5 rounded-full bg-white dark:bg-slate-900 flex items-center justify-center text-sm ring-1 ring-slate-200 dark:ring-slate-700">
                    {r.emoji}
                  </span>
                ))}
              </div>
              <span className="ml-1 font-semibold">{post.reactions_total || 0}</span>
            </button>
          ) : (
            <span>No reactions yet</span>
          )}
        </div>
        <button
          onClick={toggleComments}
          className="hover:underline"
          data-testid={`post-comments-count-${post.id}`}
        >
          {post.comments_count || 0} comment{(post.comments_count || 0) === 1 ? "" : "s"}
        </button>
      </div>

      {/* Action row */}
      <div className="mx-4 mb-2 border-t border-slate-200 dark:border-slate-800 pt-1 grid grid-cols-2 gap-1 relative">
        <div
          className="relative"
          onMouseEnter={() => setShowReactions(true)}
          onMouseLeave={() => setShowReactions(false)}
        >
          <button
            data-testid={`post-react-btn-${post.id}`}
            onPointerDown={startLongPress}
            onPointerUp={cancelLongPress}
            onPointerLeave={cancelLongPress}
            onPointerCancel={cancelLongPress}
            onContextMenu={(e) => e.preventDefault()}
            onClick={(e) => {
              // If long-press already opened the picker, don't also toggle like.
              if (longPressFired.current) {
                longPressFired.current = false;
                e.preventDefault();
                return;
              }
              react(viewerReaction || "like");
            }}
            className={`w-full flex items-center justify-center gap-2 py-2 rounded-lg font-semibold text-sm transition-colors duration-200 select-none ${
              viewerReactionMeta
                ? `${viewerReactionMeta.color} bg-slate-100 dark:bg-slate-800`
                : "text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
            }`}
          >
            {viewerReactionMeta ? (
              <>
                <span className="text-base">{viewerReactionMeta.emoji}</span>
                <span>{viewerReactionMeta.label}</span>
              </>
            ) : (
              <>
                <ThumbsUp size={18} weight="bold" /> Like
              </>
            )}
          </button>
          {showReactions && (
            <>
              {/* Backdrop that closes the picker when the user taps elsewhere
                  on touch devices (hover handles desktop). */}
              <div
                className="fixed inset-0 z-10 sm:hidden"
                onClick={() => setShowReactions(false)}
              />
              <div
                data-testid={`post-reaction-picker-${post.id}`}
                className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 flex items-center gap-0.5 sm:gap-1 px-1.5 sm:px-2 py-1.5 rounded-full bg-white dark:bg-slate-800 shadow-xl border border-slate-200 dark:border-slate-700 z-20 whitespace-nowrap max-w-[calc(100vw-2rem)]"
              >
                {REACTIONS.map((r) => (
                  <button
                    key={r.key}
                    data-testid={`post-react-${r.key}-${post.id}`}
                    onClick={() => react(r.key)}
                    title={r.label}
                    className="text-xl sm:text-2xl hover:scale-125 transition-transform duration-150 px-0.5 sm:px-1"
                  >
                    {r.emoji}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        <button
          data-testid={`post-comment-btn-${post.id}`}
          onClick={toggleComments}
          className="flex items-center justify-center gap-2 py-2 rounded-lg font-semibold text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-200"
        >
          <ChatCircle size={18} weight="bold" /> Comment
        </button>
      </div>

      {/* Comments */}
      {showComments && (
        <div className="border-t border-slate-200 dark:border-slate-800 px-4 py-3 space-y-3 bg-slate-50/50 dark:bg-slate-950/40">
          {comments === null && <div className="text-sm text-slate-500">Loading comments…</div>}
          {comments && comments.length === 0 && (
            <div className="text-sm text-slate-500 dark:text-slate-400">Be the first to comment on this ad.</div>
          )}
          {comments && comments.map((c) => {
            const cAuthor = c.author || { username: "user" };
            const cMine = c.user_id === user?.id || user?.role === "admin";
            const cViewerMeta = REACTIONS.find((r) => r.key === c.viewer_reaction);
            const cTop = [...REACTIONS]
              .map((r) => ({ ...r, count: c.reaction_counts?.[r.key] || 0 }))
              .filter((r) => r.count > 0)
              .sort((a, b) => b.count - a.count)
              .slice(0, 3);
            const cTotal = c.reactions_total ?? c.likes_count ?? 0;
            return (
              <div key={c.id} className="flex items-start gap-2" data-testid={`comment-${c.id}`}>
                <Link to={`/u/${cAuthor.id}`}>
                  <Avatar user={cAuthor} size={32} />
                </Link>
                <div className="flex-1 min-w-0">
                  <div className="relative inline-block max-w-full rounded-2xl bg-slate-100 dark:bg-slate-800 px-3 py-2">
                    <UserHoverCard userId={cAuthor.id} username={cAuthor.username}>
                      <Link to={`/u/${cAuthor.id}`} className="font-bold text-sm text-slate-900 dark:text-white hover:underline inline-flex items-center gap-1">
                        {cAuthor.username}
                        <UserBadge user={cAuthor} size={12} />
                        <AchievementBadges userId={cAuthor.id} size={12} />
                      </Link>
                    </UserHoverCard>
                    <div className="text-sm text-slate-800 dark:text-slate-200 whitespace-pre-wrap break-words">{c.body}</div>
                    {cTotal > 0 && (
                      <div
                        data-testid={`comment-reactions-summary-${c.id}`}
                        className="absolute -bottom-2 right-1 flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-sm text-[11px] text-slate-600 dark:text-slate-300"
                      >
                        {cTop.map((r) => (
                          <span key={r.key} className="text-sm leading-none">{r.emoji}</span>
                        ))}
                        <span className="ml-0.5 font-semibold">{cTotal}</span>
                      </div>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-500 dark:text-slate-400 pl-3 pt-1.5 flex items-center gap-3 relative">
                    <span>{formatTime(c.created_at)}</span>
                    <CommentReactButton comment={c} viewerMeta={cViewerMeta} onReact={(k) => likeComment(c.id, k)} />
                    {cMine && (
                      <button
                        data-testid={`comment-delete-${c.id}`}
                        onClick={() => deleteComment(c.id)}
                        className="hover:text-red-500 font-semibold"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          <form onSubmit={submitComment} className="flex items-center gap-2 pt-1">
            <Avatar user={user} size={32} />
            <div className="flex-1 flex items-center gap-1 rounded-full bg-slate-100 dark:bg-slate-800 border border-transparent focus-within:border-blue-500 px-2 pr-1">
              <input
                data-testid={`comment-input-${post.id}`}
                value={commentBody}
                onChange={(e) => setCommentBody(e.target.value)}
                placeholder="Write a comment…"
                className="flex-1 bg-transparent focus:outline-none px-2 py-2 text-sm text-slate-900 dark:text-slate-100"
                maxLength={1000}
              />
              <EmojiPicker onPick={(em) => setCommentBody((b) => (b || "") + em)} />
            </div>
            <button
              type="submit"
              disabled={posting || !commentBody.trim()}
              data-testid={`comment-submit-${post.id}`}
              className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-blue-700 hover:bg-blue-600 disabled:opacity-40 text-white transition-colors duration-200"
              aria-label="Send comment"
            >
              <ChatCircle size={16} weight="fill" />
            </button>
          </form>
        </div>
      )}
    </article>
    {likesOpen && <LikesModal postId={post.id} onClose={() => setLikesOpen(false)} />}
    </>
  );
}

function CommentReactButton({ comment, viewerMeta, onReact }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="relative inline-block"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        data-testid={`comment-like-${comment.id}`}
        onClick={() => onReact(viewerMeta ? viewerMeta.key : "like")}
        className={`inline-flex items-center gap-1 font-bold hover:underline ${viewerMeta ? viewerMeta.color : "hover:text-blue-500"}`}
      >
        {viewerMeta ? (
          <>
            <span className="text-sm leading-none">{viewerMeta.emoji}</span>
            <span>{viewerMeta.label}</span>
          </>
        ) : (
          <>
            <ThumbsUp size={12} weight="bold" /> Like
          </>
        )}
      </button>
      {open && (
        <div
          data-testid={`comment-reaction-picker-${comment.id}`}
          className="absolute bottom-full left-0 mb-1 flex items-center gap-1 px-2 py-1.5 rounded-full bg-white dark:bg-slate-800 shadow-xl border border-slate-200 dark:border-slate-700 z-20"
        >
          {REACTIONS.map((r) => (
            <button
              key={r.key}
              data-testid={`comment-react-${r.key}-${comment.id}`}
              onClick={() => { setOpen(false); onReact(r.key); }}
              title={r.label}
              className="text-xl hover:scale-125 transition-transform duration-150 px-0.5"
            >
              {r.emoji}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
